import { apiFetch } from './client';
import type { SaveEstimateInput } from '@shared/schemas';

export type SavedEstimateMaterial = {
  id: string;
  materialId: string;
  materialNameSnapshot: string;
  unitId: string;
  unitNameSnapshot: string;
  unitCategorySnapshot: 'weight' | 'purity' | 'count';
  quantity: number;
  ratePaise: number;
  discountPercent: number;
  gstPercent: number;
  basePaise: number;
  discountedPaise: number;
  gstPaise: number;
  lineTotalPaise: number;
};

export type SavedEstimateItem = {
  id: string;
  itemName: string;
  materials: SavedEstimateMaterial[];
  basePaise: number;
  discountedPaise: number;
  gstPaise: number;
  itemTotalPaise: number;
};

export type EstimateTemplateSnapshot = {
  businessName: string;
  businessType: string | null;
  contact: string | null;
  gstin: string | null;
  whatsappNumber: string | null;
  logoBlobUrl: string | null;
};

export type SavedEstimate = {
  id: string;
  estimateNumber: string;
  customerName: string | null;
  customerWhatsapp: string | null;
  items: SavedEstimateItem[];
  subtotalPaise: number;
  estimateDiscountBasisPoints: number;
  estimateDiscountPaise: number;
  totalPaise: number;
  templateSnapshot: EstimateTemplateSnapshot;
  status: 'draft' | 'saved';
  shareToken: string | null;
  shareExpiresAt: number | null;
  createdAt: number;
  updatedAt: number;
};

export type EstimateSummary = {
  id: string;
  estimateNumber: string;
  customerName: string | null;
  customerWhatsapp: string | null;
  totalPaise: number;
  createdAt: number;
  status: 'draft' | 'saved';
};

export type EstimatesPage = {
  estimates: EstimateSummary[];
  nextCursor: { cursorTs: number; cursorId: string } | null;
};

export type EstimatesQuery = {
  q?: string;
  // Inclusive epoch-ms bounds. UI converts a calendar day in user's local TZ
  // to start-of-day / end-of-day before passing through.
  dateFrom?: number;
  dateTo?: number;
  cursorTs?: number;
  cursorId?: string;
  limit?: number;
};

export type PublicShareEstimate = {
  id: string;
  estimateNumber: string;
  customerName: string | null;
  items: SavedEstimateItem[];
  subtotalPaise: number;
  estimateDiscountBasisPoints: number;
  estimateDiscountPaise: number;
  totalPaise: number;
  templateSnapshot: EstimateTemplateSnapshot;
  createdAt: number;
};

export function saveEstimate(input: SaveEstimateInput): Promise<{ estimate: SavedEstimate }> {
  // No trailing slash: Hono v4 mounts sub-router under /api/estimates and only the
  // un-suffixed path resolves the root POST handler.
  return apiFetch('/api/estimates', { method: 'POST', json: input });
}

export function fetchEstimates(query: EstimatesQuery = {}): Promise<EstimatesPage> {
  const params = new URLSearchParams();
  if (query.q) params.set('q', query.q);
  if (query.dateFrom !== undefined) params.set('dateFrom', String(query.dateFrom));
  if (query.dateTo !== undefined) params.set('dateTo', String(query.dateTo));
  if (query.cursorTs !== undefined) params.set('cursorTs', String(query.cursorTs));
  if (query.cursorId) params.set('cursorId', query.cursorId);
  if (query.limit !== undefined) params.set('limit', String(query.limit));
  const qs = params.toString();
  return apiFetch(`/api/estimates${qs ? `?${qs}` : ''}`);
}

export function fetchEstimate(id: string): Promise<{ estimate: SavedEstimate }> {
  return apiFetch(`/api/estimates/${encodeURIComponent(id)}`);
}

// Autofill context for the new-estimate form. lastRates is keyed by
// `${materialId}__${unitId}` so the client can look up the most recent rate
// when seeding a new line. Item names ride along to backfill the datalist
// suggestions even on a fresh device / new origin (post-domain migration).
export type RecentContext = {
  itemNames: string[];
  lastRates: Record<string, number>;
};

export function fetchRecentContext(): Promise<RecentContext> {
  return apiFetch('/api/estimates/recent-context');
}

export function createShareToken(
  id: string,
): Promise<{ shareToken: string; expiresAt: number | null }> {
  return apiFetch(`/api/estimates/${encodeURIComponent(id)}/share`, { method: 'POST' });
}

export function revokeShareToken(id: string): Promise<{ ok: true }> {
  return apiFetch(`/api/estimates/${encodeURIComponent(id)}/share`, { method: 'DELETE' });
}

export function fetchPublicShare(token: string): Promise<{ estimate: PublicShareEstimate }> {
  return apiFetch(`/api/share/${encodeURIComponent(token)}`);
}
